import type {
  BulletListNode,
  InlineNode,
  ListItemNode,
  ParagraphNode,
  RichTextDoc,
  RichTextMark,
} from './ast';

const LIST_MARKER = /^[-*]\s/;

export function parseMarkdown(input: string): RichTextDoc {
  if (!input || !input.trim()) return [];
  const lines = input.replace(/\r\n/g, '\n').split('\n');
  return parseBlocks(lines);
}

function parseBlocks(lines: string[]): RichTextDoc {
  const out: RichTextDoc = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) break;
    if (line.trim() === '') {
      i++;
      continue;
    }
    if (LIST_MARKER.test(line)) {
      const items: ListItemNode[] = [];
      while (i < lines.length) {
        const cur = lines[i];
        if (cur === undefined || !LIST_MARKER.test(cur)) break;
        const content = cur.slice(2);
        const paragraph: ParagraphNode = {
          type: 'paragraph',
          children: parseInline(content),
        };
        items.push({ type: 'listItem', children: [paragraph] });
        i++;
      }
      const list: BulletListNode = { type: 'bulletList', children: items };
      out.push(list);
      continue;
    }
    const inlineNodes: InlineNode[] = [];
    let first = true;
    while (i < lines.length) {
      const cur = lines[i];
      if (cur === undefined || cur.trim() === '' || LIST_MARKER.test(cur)) break;
      if (!first) inlineNodes.push({ type: 'hardBreak' });
      inlineNodes.push(...parseInline(cur));
      first = false;
      i++;
    }
    out.push({ type: 'paragraph', children: inlineNodes });
  }
  return out;
}

function parseInline(text: string): InlineNode[] {
  const out: InlineNode[] = [];
  let buf = '';
  const flush = () => {
    if (buf.length > 0) {
      out.push({ type: 'text', text: buf });
      buf = '';
    }
  };

  let i = 0;
  while (i < text.length) {
    const matched = tryMatch(text, i);
    if (matched) {
      flush();
      out.push({ type: 'text', text: matched.content, marks: matched.marks });
      i = matched.endIndex;
      continue;
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}

interface InlineMatch {
  content: string;
  marks: RichTextMark[];
  endIndex: number;
}

function tryMatch(text: string, i: number): InlineMatch | null {
  if (text.startsWith('***', i)) {
    const end = findClosing(text, i + 3, '***');
    if (end !== -1 && !isSpace(text[i + 3]) && !isSpace(text[end - 1])) {
      return {
        content: text.slice(i + 3, end),
        marks: ['bold', 'italic'],
        endIndex: end + 3,
      };
    }
  }
  if (text.startsWith('**', i)) {
    const end = findClosing(text, i + 2, '**');
    if (end !== -1 && !isSpace(text[i + 2]) && !isSpace(text[end - 1])) {
      return {
        content: text.slice(i + 2, end),
        marks: ['bold'],
        endIndex: end + 2,
      };
    }
  }
  const ch = text[i];
  if (ch === '*' || ch === '_') {
    const next = text[i + 1];
    if (next !== undefined && next !== ' ' && next !== ch) {
      const end = findClosingSingle(text, i + 1, ch);
      if (end !== -1 && !isSpace(text[end - 1])) {
        return {
          content: text.slice(i + 1, end),
          marks: ['italic'],
          endIndex: end + 1,
        };
      }
    }
  }
  return null;
}

function findClosing(text: string, from: number, delim: string): number {
  for (let j = from; j <= text.length - delim.length; j++) {
    if (text.startsWith(delim, j)) return j;
  }
  return -1;
}

function findClosingSingle(text: string, from: number, delim: string): number {
  for (let j = from; j < text.length; j++) {
    if (text[j] === delim) return j;
  }
  return -1;
}

function isSpace(ch: string | undefined): boolean {
  return ch === undefined || ch === ' ' || ch === '\t';
}
